import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContactDetail, UpdateContactPayload } from '../types';

import { convertKeysToSnakeCase } from '@shared/utils';
import { AddressBodyPayload, AddressNew } from '@erm/cluster';
import { CONTACT_URL } from '@data-access/shared';

@Injectable()
export class ContactService {
  private readonly _http = inject(HttpClient);

  /**
   * Fetches contact details by contact ID.
   * @param searchTerm - The term to search for in contact names.
   * @returns An observable of an array of ContactDetail objects.
   */
  loadContact(contactId: string) {
    return this._http.get<ContactDetail>(
      `${CONTACT_URL}/${contactId}/profile/`,
    );
  }
  /**
   * Updates contact details for a given contact ID.
   * @param degrees and phones
   * @returns An observable of an array of ContactDetail
   */
  updateContact(contactId: string, data: UpdateContactPayload) {
    return this._http.put<ContactDetail>(
      `${CONTACT_URL}/${contactId}/profile/`,
      convertKeysToSnakeCase(data),
    );
  }

  /**
   * Updates the primary and additional contact IDs for a given contact ID.
   * @param contactId - The ID of the contact to update.
   * @param data - The data to update the contact with.
   * @returns An observable of the updated ContactDetail.
   */
  updatePIContact(contactId: string, data: UpdateContactPayload) {
    return this._http.put<ContactDetail>(
      `${CONTACT_URL}/${contactId}/`,
      convertKeysToSnakeCase(data),
    );
  }

  /**
   * Creates a new address for a contact.
   * @param userId - The ID of the user/contact.
   * @param data - The address data to create.
   * @returns An observable of the created AddressNew.
   */
  addAddress(userId: string, data: AddressBodyPayload) {
    return this._http.post<AddressNew>(
      `${CONTACT_URL}/${userId}/address/`,
      convertKeysToSnakeCase(data),
    );
  }

  /**
   * Updates an existing address for a contact.
   * @param userId - The ID of the user/contact.
   * @param addressId - The ID of the address to update.
   * @param data - The address data to update.
   * @returns An observable of the updated AddressNew.
   */
  updateAddress(userId: string, addressId: string, data: AddressBodyPayload) {
    return this._http.put<AddressNew>(
      `${CONTACT_URL}/${userId}/address/${addressId}/`,
      convertKeysToSnakeCase(data),
    );
  }

  /**
   * Deletes an address for a contact.
   * @param userId - The ID of the user/contact.
   * @param addressId - The ID of the address to delete.
   * @returns An observable of the deletion response.
   */
  deleteAddress(userId: string, addressId: string) {
    return this._http.delete(`${CONTACT_URL}/${userId}/address/${addressId}/`);
  }
}
